export const MEGABYTE = 1024 * 1024;

export const recordingPolicy = {
  audio: {
    /**
     * Hard stop for recordings (in milliseconds).
     * Clips beyond this length should be auto-stopped and trimmed.
     */
    maxDurationMs: 3 * 60 * 1000,
    /**
     * Threshold for showing an in-progress warning to the user.
     */
    warnAtMs: 2.5 * 60 * 1000,
    /**
     * Optional file size guardrail for uploads (allows future enforcement).
     */
    maxFileSizeBytes: 8 * MEGABYTE,
  },
  video: {
    /**
     * Hard stop for local capture (in milliseconds).
     * 
     * Camera recordings are limited to 4 minutes to balance:
     * - User experience (allows full exercise demonstrations)
     * - File size constraints (with compression, fits within 150MB limit)
     * - Processing time and upload reliability
     */
    maxDurationMs: 4 * 60 * 1000, // 4 minutes
    /**
     * Warn users when approaching the duration limit.
     * Warning appears at 3.5 minutes to give users time to finish their recording.
     */
    warnAtMs: 3.5 * 60 * 1000, // 3.5 minutes
    /**
     * Maximum file size for video uploads (150MB).
     * 
     * This limit balances:
     * - Modern phone capabilities (1080p/4K recording)
     * - User experience (allows 3-5 minute videos at good quality)
     * - Firebase Storage costs (~$0.031 per 100MB video stored for 1 year)
     * - Industry standards (most platforms allow 100-500MB)
     * 
     * Compression profiles will attempt to reduce larger videos to meet this limit.
     */
    maxFileSizeBytes: 150 * MEGABYTE,
    /**
     * If a source video exceeds this size, prompt the user before continuing.
     * Set to 100MB to warn users about very large uploads that may take longer.
     */
    largeFilePromptThresholdBytes: 100 * MEGABYTE,
    /**
     * Compression profiles are attempted in order until the result meets the size constraint.
     * Values mirror react-native-compressor options.
     */
    compressionProfiles: [
      {
        label: 'balanced',
        maxDimension: 1280,
        bitrate: 1_000_000,
      },
      {
        label: 'aggressive',
        maxDimension: 1080,
        bitrate: 750_000,
      },
      {
        label: 'fallback',
        maxDimension: 720,
        bitrate: 500_000,
      },
    ],
  },
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const parseDurationToken = token => {
  const value = parseInt(token, 10);
  return Number.isFinite(value) && value >= 0 ? value : 0;
};

export function durationStringToMs(duration) {
  if (typeof duration !== 'string' || !duration.length) {
    return 0;
  }

  const segments = duration.split(':').map(parseDurationToken);
  if (segments.length === 0) {
    return 0;
  }

  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  if (segments.length === 3) {
    [hours, minutes, seconds] = segments;
  } else if (segments.length === 2) {
    [minutes, seconds] = segments;
  } else {
    seconds = segments[0];
  }

  return (
    clamp(hours, 0, 99) * 3600 * 1000 +
    clamp(minutes, 0, 59) * 60 * 1000 +
    clamp(seconds, 0, 59) * 1000
  );
}

export function msToDurationString(milliseconds) {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
    return '00:00';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const seconds = clamp(totalSeconds % 60, 0, 59);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = clamp(totalMinutes % 60, 0, 59);
  const hours = Math.floor(totalMinutes / 60);

  const paddedSeconds = seconds.toString().padStart(2, '0');
  const paddedMinutes = minutes.toString().padStart(2, '0');

  if (hours > 0) {
    const paddedHours = clamp(hours, 0, 99).toString().padStart(2, '0');
    return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
  }

  return `${paddedMinutes}:${paddedSeconds}`;
}

export function bytesToMegabytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return 0;
  }
  return bytes / MEGABYTE;
}

export function getAudioPolicy() {
  return recordingPolicy.audio;
}

export function getVideoPolicy() {
  return recordingPolicy.video;
}

export default recordingPolicy;
